import { useEffect, useState } from 'react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import {
  getClinicTypePresentation,
  normalizeClinicType,
  type ClinicType,
  type ClinicTypePresentation,
} from '../config/clinicTypes';

type ClinicTypeState = { clinicType: ClinicType | null };

// Session-scoped cache so many components share a single /users/me read instead
// of each re-fetching the clinic type independently.
let cache: ClinicTypeState | null = null;
let inflight: Promise<ClinicTypeState> | null = null;

type FetchJson = ReturnType<typeof useApiClient>['fetchJson'];

function loadClinicType(fetchJson: FetchJson): Promise<ClinicTypeState> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchJson<{ organization?: { clinicType?: string | null } }>('/users/me')
      .then((payload) => {
        cache = { clinicType: normalizeClinicType(payload?.organization?.clinicType) };
        return cache;
      })
      .catch(() => {
        inflight = null; // don't cache a failure; allow a later retry
        return { clinicType: null } as ClinicTypeState;
      });
  }
  return inflight;
}

export function useClinicType(): {
  clinicType: ClinicType | null;
  presentation: ClinicTypePresentation;
  loading: boolean;
} {
  const { fetchJson } = useApiClient();
  const [state, setState] = useState<ClinicTypeState | null>(cache);

  useEffect(() => {
    if (cache) {
      setState(cache);
      return;
    }
    let active = true;
    void loadClinicType(fetchJson).then((next) => {
      if (active) setState(next);
    });
    return () => {
      active = false;
    };
  }, [fetchJson]);

  const clinicType = state?.clinicType ?? null;
  return {
    clinicType,
    presentation: getClinicTypePresentation(clinicType),
    loading: state === null,
  };
}

/**
 * The localized noun this clinic uses for the people it serves
 * (e.g. "Patients" for clinical verticals, "Clients" for med spas / wellness).
 */
export function usePatientTerm(): { singular: string; plural: string } {
  const { t } = useI18n();
  const { presentation } = useClinicType();
  if (presentation.patientTerm === 'client') {
    return { singular: t('term.client'), plural: t('term.clients') };
  }
  return { singular: t('term.patient'), plural: t('term.patients') };
}
